import { createSlice } from '@reduxjs/toolkit'
import { CompanyPlanningRecordLetter } from '@leadgen/models/client'
import authActions from '../auth/actions'
import actions from './actions'

export interface CompanyPlanningRecordLettersState {
  companyPlanningRecordLetters: CompanyPlanningRecordLetter[]
  companyPlanningRecordLetterIds: Record<string, boolean>
  error?: string
  status: 'initial' | 'idle' | 'loading' | 'loading-more' | 'failed'
}

export const initialState: CompanyPlanningRecordLettersState = {
  companyPlanningRecordLetters: [],
  companyPlanningRecordLetterIds: {},
  status: 'initial',
}

export const companyPlanningRecordLettersSlice = createSlice({
  name: 'companyPlanningRecordLetters',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(authActions.loggedOut.type, () => {
        return initialState
      })
      .addCase(actions.loadCompanyPlanningRecordLettersFresh.pending, state => {
        state.status = 'loading'
      })
      .addCase(actions.loadCompanyPlanningRecordLettersFresh.fulfilled, (state, action) => {
        state.status = 'idle'
        state.companyPlanningRecordLetters = action.payload.data
        state.companyPlanningRecordLetterIds = action.payload.data.reduce(
          (memo, next) => {
            memo[next.id] = true
            return memo
          },
          <Record<string, boolean>>{},
        )
        state.error = undefined
      })
      .addCase(actions.loadCompanyPlanningRecordLettersFresh.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(actions.loadCompanyPlanningRecordLettersNext.pending, state => {
        state.status = 'loading-more'
      })
      .addCase(actions.loadCompanyPlanningRecordLettersNext.fulfilled, (state, action) => {
        state.status = 'idle'
        state.companyPlanningRecordLetters.push(...action.payload.data)
        state.companyPlanningRecordLetterIds = action.payload.data.reduce((memo, next) => {
          memo[next.id] = true
          return memo
        }, state.companyPlanningRecordLetterIds)
        state.error = undefined
      })
      .addCase(actions.loadCompanyPlanningRecordLettersNext.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(actions.addCompanyPlanningRecordLetter, (state, action) => {
        if (state.companyPlanningRecordLetterIds[action.payload.id]) {
          return
        }
        if (!state.companyPlanningRecordLetters.length) {
          state.companyPlanningRecordLetterIds[action.payload.id] = true
          state.companyPlanningRecordLetters.push(action.payload)
          return
        }
        for (let i = 0, length = state.companyPlanningRecordLetters.length; i < length; i++) {
          const companyPlanningRecordLetter = state.companyPlanningRecordLetters[i]
          if (
            new Date(action.payload.processedTimestamp) >
            new Date(companyPlanningRecordLetter.processedTimestamp)
          ) {
            state.companyPlanningRecordLetterIds[action.payload.id] = true
            state.companyPlanningRecordLetters.splice(i, 0, action.payload)
            return
          }
        }
        state.companyPlanningRecordLetterIds[action.payload.id] = true
        state.companyPlanningRecordLetters.push(action.payload)
      })
      .addCase(actions.updateCompanyPlanningRecordLetter, (state, action) => {
        for (let i = 0, length = state.companyPlanningRecordLetters.length; i < length; i++) {
          const companyPlanningRecordLetter = state.companyPlanningRecordLetters[i]
          if (companyPlanningRecordLetter.id === action.payload.id) {
            state.companyPlanningRecordLetters[i] = action.payload
            break
          }
        }
      })
  },
})

export default companyPlanningRecordLettersSlice
